import { Meal } from "@/clients/models/Meal";
import { ThumbsDownFilledIcon } from "@/components/icons/thumbs-down-filled-icon";
import { ThumbsDownIcon } from "@/components/icons/thumbs-down-icon";
import { TextButton } from "@/components/ui/buttons";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { useAddDislikedMeal } from "../../hooks/use-add-disliked-meal";
import { useGetDislikedMeals } from "../../hooks/use-get-disliked-meals";
import { useRemoveDislikedMeal } from "../../hooks/use-remove-disliked-meal";

interface DislikeMealButtonProps {
  meal: Meal;
  /** Optional request ID from alternatives session; included in POST when disliking from swap view */
  requestId?: string | null;
  className?: string;
  ariaLabel?: string;
  onDislike?: () => void;
}

export const DislikeMealButton = ({
  meal,
  requestId,
  className,
  ariaLabel = "Dislike this meal",
  onDislike,
}: DislikeMealButtonProps) => {
  const { addDislikedMealAsync, isAddingDislikedMeal } = useAddDislikedMeal();
  const { removeDislikedMealAsync, isRemovingDislikedMeal } = useRemoveDislikedMeal();
  const { dislikedMealsSet, isDislikedMealsLoading } = useGetDislikedMeals();
  const currentMealId = meal?.id ?? "";
  const isDisliked = currentMealId ? dislikedMealsSet.has(currentMealId) : false;
  const { showError } = useToast();
  const handleClick = async () => {
    if (!meal?.id) {
      return;
    }
    try {
      if (isDisliked) {
        await removeDislikedMealAsync({
          meal_id: meal.id,
        });
        return;
      }

      if (isAddingDislikedMeal) {
        return;
      }

      await addDislikedMealAsync({
        meal_id: meal.id,
        ...(requestId != null && requestId !== "" && { requestId }),
      });
      onDislike?.();
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred");
      }
    }
  };

  return (
    <TextButton
      className={cn("flex h-fit items-center justify-center", className)}
      onClick={handleClick}
      disabled={isAddingDislikedMeal || isRemovingDislikedMeal || isDislikedMealsLoading}
      aria-label={ariaLabel}
    >
      {isDisliked ? (
        <ThumbsDownFilledIcon className="h-6 w-6 text-black" />
      ) : (
        <ThumbsDownIcon className="h-6 w-6 text-black" />
      )}
    </TextButton>
  );
};
